/*
 * A build's Google Play submissions (P29 phase 2, SPEC §39.4): pure model, no React. When Submit is offered, what the
 * dialog starts with (the build profile's settings for the build's environment), its checks, the request body, and the
 * words each submission is shown with. The API decides (rules, cap, confirmation); this only says it first.
 */
import type { Artifact, Build } from '@/api/schemas';
import type { Profile } from '@/api/schemasProfile';
import type { StoreApp, Submission } from '@/api/endpointsStores';
import { NEW_SUBMIT, submitError, submitFormOf, type ReleaseStatus } from '@/features/profiles/bindingSubmitModel';

export interface SubmitBuildForm {
  storeAppId: string;
  track: string;
  releaseStatus: ReleaseStatus;
  rolloutPercent: string;
  confirmProduction: boolean;
}

/** Why Submit is held back for this build, or undefined when it may be offered. */
export function submitBlockedReason(build: Pick<Build, 'status' | 'platform'>, artifacts: Pick<Artifact, 'kind'>[] | undefined): string | undefined {
  if (build.status !== 'succeeded') return 'Available once the build succeeds';
  if (!artifacts) return 'Loading the build\'s artifacts';
  if (!artifacts.some((a) => a.kind === 'aab')) return 'Only an AAB can go to Google Play: this build made an APK. Build with a profile that makes an AAB';
  return undefined;
}

/** The dialog's starting values: the store app and settings the profile names for the build's environment. */
export function submitFormDefaults(build: Pick<Build, 'environment'>, profile: Pick<Profile, 'environments'> | undefined, storeApps: Pick<StoreApp, 'id'>[]): SubmitBuildForm {
  const binding = profile?.environments.find((e) => e.environmentId === build.environment.id);
  const saved = submitFormOf(binding?.submit) ?? NEW_SUBMIT;
  const storeAppId = binding?.defaultStoreAppId && storeApps.some((s) => s.id === binding.defaultStoreAppId) ? binding.defaultStoreAppId : (storeApps[0]?.id ?? '');
  return { storeAppId, track: saved.track, releaseStatus: saved.releaseStatus, rolloutPercent: saved.rolloutPercent, confirmProduction: false };
}

export type SubmitBuildField = 'storeAppId' | 'track' | 'confirmProduction';

/** What is wrong with the form, by field (the track and rollout share share the profile's checks). */
export function submitBuildErrors(v: SubmitBuildForm): Partial<Record<SubmitBuildField, string>> {
  const errors: Partial<Record<SubmitBuildField, string>> = {};
  if (!v.storeAppId) errors.storeAppId = 'Choose the Google Play app';
  const settings = submitError({ autoSubmit: false, track: v.track, releaseStatus: v.releaseStatus, rolloutPercent: v.rolloutPercent, changesNotSentForReview: false });
  if (settings) errors.track = settings;
  if (v.track.trim() === 'production' && !v.confirmProduction) errors.confirmProduction = 'Confirm that this release reaches your customers';
  return errors;
}

/** `POST /submissions`: only the settings the request decides; the confirmation only for production. */
export function submitBuildBody(buildId: string, v: SubmitBuildForm) {
  const track = v.track.trim();
  return {
    buildId, storeAppId: v.storeAppId, track, releaseStatus: v.releaseStatus,
    ...(v.releaseStatus === 'inProgress' ? { rolloutFraction: Number(v.rolloutPercent) / 100 } : {}),
    ...(track === 'production' ? { confirmProduction: true } : {}),
  };
}

const STATUS_LABEL: Record<string, string> = {
  queued: 'Queued', submitting: 'Sending', processing: 'Processing at Google Play', in_review: 'In review', ready_to_publish: 'Ready to publish',
  published: 'Published', rejected: 'Rejected', failed: 'Failed', canceled: 'Canceled', pending_approval: 'Waiting for approval',
};
export const submissionStatusLabel = (status: string): string => STATUS_LABEL[status] ?? status;

/** "Staged to 10 %", "Draft" or "Full release": how the submission releases on its track (asked for, whatever happened). */
export function releaseText(s: Pick<Submission, 'rolloutStatus' | 'rolloutFraction'>): string {
  if (s.rolloutStatus === 'in_progress') return `Staged to ${Math.round((s.rolloutFraction ?? 0) * 100)} %`;
  return s.rolloutStatus === 'draft' ? 'Draft' : 'Full release';
}

const PROBLEM: Record<string, string> = {
  // Apps built on Bitrise (phase 4) and iOS (phase 6): Hangar records the submission but does not send it yet.
  'HGR-NOT-IMPLEMENTED': 'Hangar does not send this app\'s builds to the store yet: upload the AAB by hand from Artifacts. Your settings and rules are kept.',
  'HGR-PROVIDER-CREDENTIALS': 'Hangar\'s access to its build service was refused, so nothing was sent: ask your Hangar administrators to renew it, then submit again.',
};

/** Why a submission failed, in the client's words; the store's own message otherwise. */
export function submissionProblem(s: Pick<Submission, 'error'>): string | null {
  if (!s.error) return null;
  return PROBLEM[s.error.code] ?? (s.error.message || s.error.code);
}
